// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Calendar, Download, Users, DollarSign, FileText, TrendingUp } from 'lucide-react';
import { ProBonoReportService, ProBonoReportData, ProBonoCase, ProBonoCategory } from '@/services/proBonoReportService';
import { useToast } from '@/hooks/use-toast';

const ProBonoReport: React.FC = () => {
  const { toast } = useToast();
  const [reportData, setReportData] = useState<ProBonoReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const loadReportData = async () => {
    try {
      setLoading(true);
      const data = await ProBonoReportService.generateProBonoReport(
        dateRange.startDate || undefined,
        dateRange.endDate || undefined
      );
      setReportData(data);
    } catch (error) {
      console.error('Erro ao carregar relatório Pro Bono:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o relatório Pro Bono",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReportData();
  }, [dateRange]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const exportReport = async () => {
    try {
      const blob = await ProBonoReportService.exportProBonoReport(
        'current-user-id', // TODO: Get from auth context
        dateRange.startDate || undefined,
        dateRange.endDate || undefined
      );
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio-probono-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Sucesso",
        description: "Relatório exportado com sucesso"
      });
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      toast({
        title: "Erro",
        description: "Não foi possível exportar o relatório",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Carregando relatório Pro Bono...</p>
        </div>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="text-center py-8">
        <FileText className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum dado encontrado</h3>
        <p className="mt-1 text-sm text-gray-500">Não há casos Pro Bono para exibir no período selecionado.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatório Pro Bono</h1>
          <p className="text-gray-600">Impacto social e casos gratuitos</p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={exportReport} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Exportar PDF
          </Button>
        </div>
      </div>

      {/* Filtros de Data */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Filtros de Período
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Data Inicial</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Data Final</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumo Geral */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Casos</p>
                <p className="text-2xl font-bold text-gray-900">{reportData.totalCases}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Valor Estimado</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(reportData.totalValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Horas Trabalhadas</p>
                <p className="text-2xl font-bold text-gray-900">{reportData.totalHours}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Categorias */}
      <Card>
        <CardHeader>
          <CardTitle>Casos por Categoria</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(reportData.categories).map(([category, data]) => (
              <div key={category} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Badge 
                    variant="outline" 
                    style={{ backgroundColor: data.color + '20', color: data.color, borderColor: data.color }}
                  >
                    {category}
                  </Badge>
                  <span className="text-sm text-gray-600">{data.count} casos</span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(data.value)}
                  </span>
                  <div className="w-32">
                    <Progress 
                      value={(data.count / reportData.totalCases) * 100} 
                      className="h-2"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dados Mensais */}
      <Card>
        <CardHeader>
          <CardTitle>Evolução Mensal</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {reportData.monthlyData.map((monthData) => (
              <div key={monthData.month} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">
                    {new Date(monthData.month + '-01').toLocaleDateString('pt-BR', { 
                      year: 'numeric', 
                      month: 'long' 
                    })}
                  </h4>
                  <p className="text-sm text-gray-600">{monthData.cases} casos</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">{formatCurrency(monthData.value)}</p>
                  <p className="text-sm text-gray-600">valor estimado</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Casos Recentes */}
      <Card>
        <CardHeader>
          <CardTitle>Casos Recentes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {reportData.recentCases.map((case_) => (
              <div key={case_.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{case_.case_title}</h4>
                  <p className="text-sm text-gray-600">
                    {case_.client_name || 'Cliente não informado'}
                    {case_.social_impact_description && ` • ${case_.social_impact_description}`}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(case_.start_date).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div className="text-right">
                  <Badge variant="secondary" style={{ backgroundColor: case_.category_color + '20', color: case_.category_color }}>
                    {case_.category_name}
                  </Badge>
                  <p className="text-sm text-gray-600 mt-1">
                    {formatCurrency(case_.estimated_value)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProBonoReport;
